/**
 * NOS-57: Contact submissions inbox.
 * View and manage form submissions with status indicators, filters, bulk actions.
 */

import { useCallback, useState } from "react"
import { Link } from "react-router-dom"
import {
  Archive,
  CheckSquare,
  Loader2,
  Mail,
  MessageSquare,
  User,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { useFormSubmissions, useUpdateSubmissionStatus } from "@/hooks/useFormSubmissions"
import type { FormSubmissionWithContact, FormSubmissionStatus } from "@/hooks/useFormSubmissions"
import { toast } from "sonner"
import { PermissionGate } from "@/components/PermissionGate"
import { RequireOrg } from "@/components/RequireOrg"
import { Navigate } from "react-router-dom"

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "replied", label: "Replied" },
  { value: "archived", label: "Archived" },
] as const

const DATE_PRESETS = [
  { value: "all", label: "All time", from: undefined, to: undefined },
  { value: "7", label: "Last 7 days", daysBack: 7 },
  { value: "30", label: "Last 30 days", daysBack: 30 },
  { value: "90", label: "Last 90 days", daysBack: 90 },
] as const

function formatSubmissionDate(dateStr: string) {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) {
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  }
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  })
}

function formatFullDate(dateStr: string) {
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function StatusBadge({ status }: { status: FormSubmissionStatus }) {
  const config = {
    new: {
      variant: "default" as const,
      className: "font-semibold",
      icon: <span className="mr-1 inline-block h-2 w-2 rounded-full bg-blue-500" />,
      label: "New",
    },
    read: {
      variant: "secondary" as const,
      className: "",
      icon: null,
      label: "Read",
    },
    replied: {
      variant: "secondary" as const,
      className: "",
      icon: <MessageSquare className="mr-1 inline h-3 w-3" />,
      label: "Replied",
    },
    archived: {
      variant: "outline" as const,
      className: "text-muted-foreground",
      icon: <Archive className="mr-1 inline h-3 w-3" />,
      label: "Archived",
    },
  }
  const c = config[status]
  return (
    <Badge variant={c.variant} className={c.className}>
      {c.icon}
      {c.label}
    </Badge>
  )
}

function FormDataDisplay({ formData }: { formData: Record<string, unknown> }) {
  const entries = Object.entries(formData).filter(
    ([k]) => k !== "email" && k !== "name"
  )
  if (entries.length === 0) return null
  return (
    <div className="space-y-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </Label>
          <p className="text-sm">
            {typeof value === "string" ? value : JSON.stringify(value)}
          </p>
        </div>
      ))}
    </div>
  )
}

export function SubmissionsInboxPage() {
  return (
    <RequireOrg>
      <SubmissionsInboxPageContent />
    </RequireOrg>
  )
}

function SubmissionsInboxPageContent() {
  const [statusTab, setStatusTab] = useState<
    "all" | "new" | "read" | "replied" | "archived"
  >("all")
  const [datePreset, setDatePreset] = useState<string>("all")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [detailSubmission, setDetailSubmission] =
    useState<FormSubmissionWithContact | null>(null)

  const filters = (() => {
    if (datePreset === "all") {
      return { status: statusTab, dateFrom: undefined, dateTo: undefined }
    }
    const opt = DATE_PRESETS.find((d) => d.value === datePreset)
    const daysBack = opt && "daysBack" in opt ? opt.daysBack : null
    if (daysBack) {
      const from = new Date()
      from.setDate(from.getDate() - daysBack)
      return {
        status: statusTab,
        dateFrom: from.toISOString().slice(0, 10),
        dateTo: new Date().toISOString().slice(0, 10),
      }
    }
    return { status: statusTab, dateFrom: undefined, dateTo: undefined }
  })()

  const {
    submissions,
    loading,
    error,
    refetch,
  } = useFormSubmissions(filters)
  const { updateStatus } = useUpdateSubmissionStatus()

  const handleOpenDetail = useCallback(
    async (sub: FormSubmissionWithContact) => {
      setDetailSubmission(sub)
      if (sub.status === "new") {
        const { success, error: updateErr } = await updateStatus(sub.id, "read")
        if (success) {
          refetch()
        } else {
          toast.error(updateErr ?? "Failed to mark as read")
        }
      }
    },
    [updateStatus, refetch]
  )

  const handleMarkReplied = useCallback(
    async (id: string) => {
      const { success, error: updateErr } = await updateStatus(id, "replied")
      if (success) {
        toast.success("Marked as replied")
        refetch()
        if (detailSubmission?.id === id) {
          setDetailSubmission((s) =>
            s ? { ...s, status: "replied" as const } : null
          )
        }
      } else {
        toast.error(updateErr ?? "Failed to update")
      }
    },
    [updateStatus, refetch, detailSubmission]
  )

  const handleArchive = useCallback(
    async (id: string) => {
      const { success, error: updateErr } = await updateStatus(id, "archived")
      if (success) {
        toast.success("Archived")
        refetch()
        if (detailSubmission?.id === id) setDetailSubmission(null)
      } else {
        toast.error(updateErr ?? "Failed to archive")
      }
    },
    [updateStatus, refetch, detailSubmission]
  )

  const handleBulkMarkRead = useCallback(async () => {
    let done = 0
    for (const id of selectedIds) {
      const sub = submissions.find((s) => s.id === id)
      if (sub?.status === "new") {
        const { success } = await updateStatus(id, "read")
        if (success) done++
      }
    }
    if (done > 0) {
      toast.success(`Marked ${done} as read`)
      refetch()
      setSelectedIds(new Set())
    }
  }, [selectedIds, submissions, updateStatus, refetch])

  const handleBulkArchive = useCallback(async () => {
    let done = 0
    for (const id of selectedIds) {
      const { success } = await updateStatus(id, "archived")
      if (success) done++
    }
    if (done > 0) {
      toast.success(`Archived ${done} submissions`)
      refetch()
      setSelectedIds(new Set())
      setDetailSubmission(null)
    }
  }, [selectedIds, updateStatus, refetch])

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(submissions.map((s) => s.id)))
    }
  }

  const selectedCount = selectedIds.size
  const hasNewSelected = submissions.some(
    (s) => selectedIds.has(s.id) && s.status === "new"
  )

  return (
    <PermissionGate
      permission="can_manage_contacts"
      fallback={<Navigate to="/dashboard" replace />}
    >
      <div className="bg-background">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Contact Submissions</h2>
          {selectedCount > 0 && (
            <div className="flex items-center gap-2">
              {hasNewSelected && (
                <Button size="sm" variant="outline" onClick={handleBulkMarkRead}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Mark as read
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={handleBulkArchive}>
                <Archive className="mr-2 h-4 w-4" />
                Archive ({selectedCount})
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear selection
              </Button>
            </div>
          )}
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Tabs
            value={statusTab}
            onValueChange={(v) =>
              setStatusTab(v as "all" | "new" | "read" | "replied" | "archived")
            }
          >
            <TabsList>
              {STATUS_TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Label className="whitespace-nowrap text-sm text-muted-foreground">
              Date range:
            </Label>
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            >
              {DATE_PRESETS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-md border">
          {loading ? (
            <div className="flex justify-center gap-2 p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Loading submissions...
              </span>
            </div>
          ) : error ? (
            <p className="p-8 text-center text-destructive">{error}</p>
          ) : submissions.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">
              No submissions yet. Form submissions from your website will appear
              here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          submissions.length > 0 &&
                          selectedIds.size === submissions.length
                        }
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Form / Page</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((sub) => (
                    <TableRow
                      key={sub.id}
                      className={`cursor-pointer ${
                        sub.status === "new" ? "bg-blue-50/50 font-medium" : ""
                      }`}
                      onClick={() => handleOpenDetail(sub)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(sub.id)}
                          onCheckedChange={() => toggleSelect(sub.id)}
                          aria-label={`Select ${sub.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>
                            {sub.contact_name || sub.contact_email || "—"}
                          </span>
                          {sub.contact_email && sub.contact_name && (
                            <span className="text-xs text-muted-foreground">
                              {sub.contact_email}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {sub.page_title ?? "Contact Form"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={sub.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatSubmissionDate(sub.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Detail dialog */}
        <Dialog
          open={!!detailSubmission}
          onOpenChange={(open) => !open && setDetailSubmission(null)}
        >
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
            {detailSubmission && (
              <>
                <DialogHeader>
                  <DialogTitle>Submission details</DialogTitle>
                  <DialogDescription>
                    {formatFullDate(detailSubmission.created_at)} •{" "}
                    {detailSubmission.page_title ?? "Contact Form"}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex flex-col gap-2">
                    <Label className="text-xs text-muted-foreground">
                      Contact
                    </Label>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {detailSubmission.contact_name ||
                          detailSubmission.contact_email ||
                          "—"}
                      </span>
                    </div>
                    <a
                      href={`mailto:${detailSubmission.contact_email}`}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Mail className="h-4 w-4" />
                      {detailSubmission.contact_email ?? "—"}
                    </a>
                  </div>
                  <FormDataDisplay formData={detailSubmission.form_data} />
                </div>
                <DialogFooter>
                  {detailSubmission.contact_id ? (
                    <Button variant="outline" asChild>
                      <Link to={`/dashboard/contacts/${detailSubmission.contact_id}`}>
                        View contact profile
                      </Link>
                    </Button>
                  ) : (
                    <Button variant="outline" disabled>
                      View contact profile
                    </Button>
                  )}
                  {detailSubmission.status !== "replied" && (
                    <Button
                      variant="outline"
                      onClick={() => handleMarkReplied(detailSubmission.id)}
                    >
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Mark as Replied
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => handleArchive(detailSubmission.id)}
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  )
}
